import React, { useState, useEffect } from 'react'
import { Save, Menu } from 'lucide-react'
import { useNavigate, useLocation } from 'react-router-dom'
import Breadcrumb from '../../components/Common/Breadcrumb'
import HelpModal from '../../components/HelpModal/HelpModal'
import menuItemService from '../../services/menuItemService'
import { toast } from 'react-toastify'

const MenuItemMasterEdit = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const menuItemData = location.state?.menuItemData || {}
  const id = menuItemData.id || menuItemData._id

  const [formData, setFormData] = useState({
    menuCode: '',
    label: '',
    path: '#',
    icon: 'Circle',
    parent: '',
    sortOrder: 0,
    isActive: true,
    description: ''
  })
  const [parentOptions, setParentOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(!!id)
  const [showHelp, setShowHelp] = useState(false)

  useEffect(() => {
    const loadItem = async () => {
      if (!id) {
        setInitialLoading(false)
        return
      }
      try {
        setInitialLoading(true)
        const res = await menuItemService.getMenuItemById(id)
        if (res?.success && res.data) {
          const d = res.data
          setFormData({
            menuCode: d.menuCode || '',
            label: d.label || '',
            path: d.path || '#',
            icon: d.icon || 'Circle',
            parent: d.parent?._id || d.parent || '',
            sortOrder: d.sortOrder ?? 0,
            isActive: d.isActive !== false,
            description: d.description || ''
          })
        }
      } catch (err) {
        toast.error('Failed to load menu item')
        navigate('/settings/menu-item-master')
      } finally {
        setInitialLoading(false)
      }
    }
    loadItem()
  }, [id, navigate])

  useEffect(() => {
    const loadParents = async () => {
      try {
        const res = await menuItemService.getAllMenuItems({ includeInactive: true })
        if (res?.success && Array.isArray(res.data)) {
          setParentOptions(res.data.filter(item => (item._id || item.id) !== id).map(item => ({ value: item._id, label: item.label || item.menuCode })))
        }
      } catch (err) {
        console.error(err)
      }
    }
    loadParents()
  }, [id])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: name === 'sortOrder' ? (value === '' ? 0 : parseInt(value, 10)) : name === 'isActive' ? value === 'true' : value
    }))
  }

  const handleSave = async () => {
    if (!id) return
    if (!formData.menuCode?.trim() || !formData.label?.trim() || !formData.path?.trim()) {
      toast.error('Menu Code, Label and Path are required')
      return
    }
    try {
      setLoading(true)
      const payload = {
        menuCode: formData.menuCode.trim(),
        label: formData.label.trim(),
        path: formData.path.trim() || '#',
        icon: formData.icon?.trim() || 'Circle',
        parent: formData.parent || null,
        sortOrder: Number(formData.sortOrder) || 0,
        isActive: formData.isActive,
        description: formData.description?.trim() || ''
      }
      const res = await menuItemService.updateMenuItem(id, payload)
      if (res?.success) {
        toast.success('Menu item updated')
        navigate('/settings/menu-item-master')
      } else {
        toast.error(res?.message || 'Failed to update')
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update')
    } finally {
      setLoading(false)
    }
  }

  const handleBack = () => navigate('/settings/menu-item-master')

  const breadcrumbItems = [
    { label: 'Settings', path: '/settings', onClick: () => navigate('/settings') },
    { label: 'Masters', path: '/settings', onClick: () => navigate('/settings') },
    { label: 'Menu Item Master', path: '/settings/menu-item-master', onClick: () => navigate('/settings/menu-item-master') },
    { label: 'Edit Menu Item', path: '', isCurrent: true }
  ]

  const helpContent = (
    <>
      <h3>Edit Menu Item</h3>
      <p>Update the menu code, label, path, parent, sort order, and status. Changes apply after saving.</p>
    </>
  )

  if (initialLoading) {
    return (
      <div className="sales-dashboard">
        <div className="breadcrumb-section"><Breadcrumb items={breadcrumbItems} showBackButton={false} /></div>
        <div className="text-center py-5">Loading...</div>
      </div>
    )
  }

  if (!id) {
    return (
      <div className="sales-dashboard">
        <div className="breadcrumb-section"><Breadcrumb items={breadcrumbItems} showBackButton={false} /></div>
        <div className="text-center py-5">No menu item selected. <button type="button" className="btn btn-link" onClick={() => navigate('/settings/menu-item-master')}>Back to list</button></div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          showBackButton={false}
          rightSlot={
            <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
              <Menu size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>

      <div className="page-header">
        <div className="page-title">
          <h1>Menu Item Master Entry</h1>
          <p>Update menu configuration</p>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Menu Item Master Entry</h2>
        </div>

        <form className="form-content" onSubmit={(e) => e.preventDefault()}>
          <div className="section-header">
            <h3>Menu Item Information</h3>
          </div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="menuCode" className="form-label required">Menu Code</label>
              <input type="text" id="menuCode" name="menuCode" value={formData.menuCode} onChange={handleInputChange} className="form-input" placeholder="e.g. ROLE_MASTER" />
            </div>
            <div className="form-group">
              <label htmlFor="label" className="form-label required">Label</label>
              <input type="text" id="label" name="label" value={formData.label} onChange={handleInputChange} className="form-input" placeholder="e.g. Role Master" />
            </div>
            <div className="form-group">
              <label htmlFor="path" className="form-label required">Path</label>
              <input type="text" id="path" name="path" value={formData.path} onChange={handleInputChange} className="form-input" placeholder="e.g. /settings/role-master" />
            </div>
            <div className="form-group">
              <label htmlFor="icon" className="form-label">Icon</label>
              <input type="text" id="icon" name="icon" value={formData.icon} onChange={handleInputChange} className="form-input" placeholder="e.g. Shield" />
            </div>
          </div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="parent" className="form-label">Parent</label>
              <select id="parent" name="parent" value={formData.parent} onChange={handleInputChange} className="form-input">
                <option value="">None</option>
                {parentOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="sortOrder" className="form-label">Sort Order</label>
              <input type="number" id="sortOrder" name="sortOrder" min={0} value={formData.sortOrder} onChange={handleInputChange} className="form-input" />
            </div>
            <div className="form-group">
              <label htmlFor="isActive" className="form-label">Status</label>
              <select id="isActive" name="isActive" value={String(formData.isActive)} onChange={handleInputChange} className="form-input">
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="description" className="form-label">Description</label>
              <textarea id="description" name="description" rows={2} value={formData.description} onChange={handleInputChange} className="form-input" placeholder="Optional description" />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-primary" onClick={handleSave} disabled={loading}>
              <Save size={16} />
              {loading ? 'Saving...' : 'Save'}
            </button>
            <button type="button" className="btn btn-outline-secondary" onClick={handleBack}>Cancel</button>
          </div>
        </form>
      </div>

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Edit Menu Item – Help">
        {helpContent}
      </HelpModal>
    </div>
  )
}

export default MenuItemMasterEdit
